import React, { useRef, useState, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon, Sparkles, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else line = test;
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (lines.length === maxLines && words.length) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length) last = last.slice(0, -1);
    lines[maxLines - 1] = last + "…";
  }
  return lines;
}

// Viral acquisition card — promotes the GAME itself with a "Built with AI on GameView" watermark.
export default function ShareGameCard({ game, gameId, trigger }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const playUrl = `${window.location.origin}/app/arcade/${gameId}`;
  const ogUrl = `${window.location.origin}/api/og/game/${gameId}`;
  const shortUrl = playUrl.replace(/^https?:\/\//, "");
  const shareText = `I built "${game.title}" with AI on GameView Studio — play it free!`;

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const W = 1200, H = 630;
    const bg = game.world?.bgColor || "#0b1026";
    const ac = game.world?.accent || "#7c5cff";
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, bg); grad.addColorStop(1, ac);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(0,0,0,0.42)"; ctx.fillRect(0, 0, W, H);

    // brand top-left
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.textBaseline = "top";
    ctx.font = "bold 34px Inter, system-ui, sans-serif";
    ctx.fillText("🎮 GAMEVIEW STUDIO", 60, 50);

    // "Built with AI" pill top-right
    ctx.font = "bold 26px Inter, system-ui, sans-serif";
    const pill = "✨ Built with AI";
    const pw = ctx.measureText(pill).width + 44;
    const px = W - pw - 60, py = 46;
    ctx.fillStyle = "rgba(124,92,255,0.85)";
    ctx.beginPath(); ctx.roundRect(px, py, pw, 50, 25); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.fillText(pill, px + 22, py + 11);

    // cover emoji
    ctx.textBaseline = "middle"; ctx.font = "150px serif";
    ctx.fillText(game.cover_emoji || "🎮", 70, 300);

    // title
    ctx.fillStyle = "#fff"; ctx.textBaseline = "alphabetic";
    ctx.font = "bold 64px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(game.title, 22), 300, 250);

    // by author
    ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.font = "32px Inter, system-ui, sans-serif";
    ctx.fillText(`by ${truncate(game.owner_name || "a GameView creator", 34)}`, 300, 300);

    // description (2 lines)
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "30px Inter, system-ui, sans-serif";
    const desc = game.description || game.tagline || "A new game made with AI. Jump in and play!";
    wrapLines(ctx, desc, W - 360, 2).forEach((ln, i) => ctx.fillText(ln, 300, 350 + i * 42));

    // stats
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 34px Inter, system-ui, sans-serif";
    const rating = game.rating > 0 ? `★ ${Number(game.rating).toFixed(1)}` : "★ New";
    ctx.fillText(`▶ ${game.plays || 0} plays     ${rating}`, 300, 470);

    // footer CTA
    ctx.fillStyle = "rgba(255,255,255,0.78)"; ctx.font = "30px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Play free — ${truncate(shortUrl, 44)}`, 60, 575);
  }, [game, shortUrl]);

  useEffect(() => { if (open) { const t = setTimeout(draw, 60); return () => clearTimeout(t); } }, [open, draw]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await toBlob(); if (!blob) { toast.error("Couldn't render the card. Try again."); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(game.title || "gameview").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-gameview.png`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Promo card downloaded!");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(ogUrl); toast.success("Link copied — paste it anywhere!"); }
    catch { toast.error("Couldn't copy the link."); }
  };

  const nativeShare = async () => {
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "gameview-game.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: game.title, text: shareText, url: ogUrl });
      } else {
        await navigator.share({ title: game.title, text: shareText, url: ogUrl });
      }
    } catch { /* cancelled / unsupported */ }
  };

  const SOCIALS = [
    { key: "x", label: "X", color: "#000000", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(ogUrl)}` },
    { key: "facebook", label: "Facebook", color: "#1877f2", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}` },
    { key: "reddit", label: "Reddit", color: "#ff4500", href: `https://www.reddit.com/submit?url=${encodeURIComponent(ogUrl)}&title=${encodeURIComponent(shareText)}` },
    { key: "whatsapp", label: "WhatsApp", color: "#25d366", href: `https://wa.me/?text=${encodeURIComponent(shareText + " " + ogUrl)}` },
    { key: "telegram", label: "Telegram", color: "#229ed9", href: `https://t.me/share/url?url=${encodeURIComponent(ogUrl)}&text=${encodeURIComponent(shareText)}` },
  ];
  const openSocial = (href) => window.open(href, "_blank", "noopener,noreferrer");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="rounded-full gap-1.5 border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]" data-testid="gp-share-game-btn">
            <Megaphone className="w-4 h-4" /> Share game
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="gp-share-game-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Share your game</DialogTitle>
          <DialogDescription>Spread the word — every share is a new player. The card is auto-branded "Built with AI on GameView".</DialogDescription>
        </DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="gp-share-game-canvas" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-game-download"><Download className="w-4 h-4" /> Download</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-game-copy-link"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-game-native"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
        <div>
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Post directly to</div>
          <div className="flex flex-wrap gap-2">
            {SOCIALS.map((s) => (
              <button key={s.key} onClick={() => openSocial(s.href)} data-testid={`gp-share-social-${s.key}`}
                className="px-3.5 py-2 rounded-full text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: s.color }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
